import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {Subject, Subscription} from "rxjs";
import {debounceTime, distinctUntilChanged} from "rxjs/operators";
import Swal from "sweetalert2";
import * as moment from 'moment';
import {AthleteService} from "../../services/athlete/athlete.service";
import {CompanyService} from "../../services/company/company.service";
import {SeasonService} from "../../services/season/season.service";
import {CategoryService} from "../../services/category/category.service";
import {UtilsService} from "../../services/utils";
import {Athlete} from "../../models/athlete";
import {AthleteSeason} from "../../models/athleteSeason";
import {Category} from "../../models/category";
import {CompanyDocument} from "../../models/CompanyDocument";
import {Season} from "../../models/season";
import {
    athleteMembershipExpiration,
    enrollmentAmount,
    enrollmentDisplayStatus,
    enrollmentStatusIcon,
    enrollmentStatusLabel,
    formatEuro,
} from "../companies-subscriptions/enrollment";

type QuickFilter = 'all' | 'missingPayment' | 'membership' | 'expiredDocument';

@Component({
    selector: 'app-companies-allusers',
    templateUrl: './companies-allusers.component.html',
    styleUrls: ['./companies-allusers.component.scss']
})
export class CompaniesAllusersComponent implements OnInit, OnDestroy {

    companyId = localStorage.getItem('wisper_companyId') ?? '';

    athletes: Athlete[] = [];
    totalRecords = 0;
    loading = true;
    loadError = false;
    exporting = false;

    seasons: Season[] = [];
    categories: Category[] = [];
    primaryDocumentExists = false;

    search = '';
    selectedSeasonId?: string;
    selectedCategoryId?: string;
    quickFilter: QuickFilter = 'all';
    counts: Partial<Record<QuickFilter, number>> = {};

    page = 0;
    readonly pageSizes = [30, 50, 100];
    pageSize = 30;

    readonly skeletonRows = [1, 2, 3, 4, 5, 6, 7, 8];
    readonly formatEuro = formatEuro;
    readonly displayStatus = enrollmentDisplayStatus;
    readonly statusLabel = enrollmentStatusLabel;
    readonly statusIcon = enrollmentStatusIcon;
    readonly amount = enrollmentAmount;

    private searchInput$ = new Subject<string>();
    private searchSub?: Subscription;
    private requestId = 0;

    constructor(
        private athleteService: AthleteService,
        private companyService: CompanyService,
        private seasonService: SeasonService,
        private categoryService: CategoryService,
        private route: ActivatedRoute,
        private router: Router,
        public utils: UtilsService,
    ) {
    }

    async ngOnInit() {
        if (this.route.snapshot.queryParamMap.get('missingPayment') === 'true') this.quickFilter = 'missingPayment';

        this.searchSub = this.searchInput$
            .pipe(debounceTime(350), distinctUntilChanged())
            .subscribe(() => this.applyFilters());

        const [documents, seasonsResponse, categories] = await Promise.all([
            this.companyService.getCompanyDocuments(this.companyId).catch(() => [] as CompanyDocument[]),
            this.seasonService.getSeasons(this.companyId, {$orderby: 'startDate desc'}),
            this.categoryService.getCategories(this.companyId),
        ]);
        this.primaryDocumentExists = (documents ?? []).some((document: CompanyDocument) => document.primaryDocument === 1);
        this.seasons = seasonsResponse?.success ? Season.fromApiList(seasonsResponse.data) : [];
        this.categories = categories.map(item => new Category(item));

        await this.load();
    }

    ngOnDestroy() {
        this.searchSub?.unsubscribe();
    }

    // ---- Season / category context ----

    get currentSeason(): Season | undefined {
        return this.seasons.find(season => !!season.isCurrent);
    }

    /** Season whose category and payment are shown in the table. */
    get focusSeason(): Season | undefined {
        return this.seasons.find(season => season.id === this.selectedSeasonId) ?? this.currentSeason;
    }

    get categoryOptions(): {id: string, name: string}[] {
        const seasonId = this.focusSeason?.id;
        return this.categories
            .filter(category => category.parentId && category.seasonId === seasonId)
            .map(category => ({id: category.id!, name: this.categoryLabel(category.id)}))
            .sort((a, b) => a.name.localeCompare(b.name, 'it', {numeric: true}));
    }

    categoryLabel(categoryId?: string): string {
        const category = this.categories.find(item => item.id === categoryId);
        if (!category) return '';
        const parent = this.categories.find(item => item.id === category.parentId);
        return parent ? `${parent.name} · ${category.name}` : category.name;
    }

    focusEnrollment(athlete: Athlete): AthleteSeason | undefined {
        const seasonId = this.focusSeason?.id;
        return (athlete.athleteSeasons ?? []).find(enrollment => enrollment.seasonId === seasonId);
    }

    // ---- Loading ----

    async load() {
        const requestId = ++this.requestId;
        this.loading = true;
        this.loadError = false;

        const base = this.baseParams();
        const [list, ...counts] = await Promise.all([
            this.athleteService.listAthletes({
                ...base,
                ...this.quickFilterParams(this.quickFilter),
                $skip: this.page * this.pageSize,
                $top: this.pageSize,
            }),
            ...this.quickFilters.map(filter =>
                this.athleteService.listAthletes({...base, ...this.quickFilterParams(filter), $top: 1})),
        ]);
        if (requestId !== this.requestId) return;

        this.loadError = !list;
        this.athletes = list?.rows ?? [];
        this.totalRecords = list?.count ?? 0;
        this.counts = {};
        this.quickFilters.forEach((filter, index) => this.counts[filter] = counts[index]?.count);
        this.loading = false;
    }

    get quickFilters(): QuickFilter[] {
        return this.primaryDocumentExists
            ? ['all', 'missingPayment', 'membership', 'expiredDocument']
            : ['all', 'missingPayment', 'membership'];
    }

    quickFilterLabel(filter: QuickFilter): string {
        return {
            all: 'Tutti',
            missingPayment: 'Pagamento mancante',
            membership: 'Con tesseramento',
            expiredDocument: 'Documento scaduto',
        }[filter];
    }

    onSearch(value: string) {
        this.searchInput$.next(value.trim());
    }

    applyFilters() {
        this.page = 0;
        void this.load();
    }

    onSeasonChange() {
        // Categories belong to a season: drop a selection that no longer applies.
        if (!this.categoryOptions.some(option => option.id === this.selectedCategoryId)) this.selectedCategoryId = undefined;
        this.applyFilters();
    }

    setQuickFilter(filter: QuickFilter) {
        if (this.quickFilter === filter) return;
        this.quickFilter = filter;
        this.applyFilters();
    }

    get hasActiveFilters(): boolean {
        return !!this.search.trim() || !!this.selectedSeasonId || !!this.selectedCategoryId || this.quickFilter !== 'all';
    }

    resetFilters() {
        this.search = '';
        this.selectedSeasonId = undefined;
        this.selectedCategoryId = undefined;
        this.quickFilter = 'all';
        this.applyFilters();
    }

    // ---- Pagination ----

    get pageCount(): number {
        return Math.max(1, Math.ceil(this.totalRecords / this.pageSize));
    }

    get rangeLabel(): string {
        if (!this.totalRecords) return '0 atleti';
        const first = this.page * this.pageSize + 1;
        const last = Math.min(this.totalRecords, first + this.pageSize - 1);
        return `${first}–${last} di ${this.totalRecords}`;
    }

    goToPage(page: number) {
        if (page < 0 || page >= this.pageCount || page === this.page) return;
        this.page = page;
        void this.load();
    }

    onPageSizeChange() {
        this.applyFilters();
    }

    // ---- Row helpers ----

    initials(athlete: Athlete): string {
        return `${athlete.firstName?.[0] ?? ''}${athlete.lastName?.[0] ?? ''}`.toUpperCase() || '?';
    }

    statusClasses(status?: string): string {
        switch (status) {
            case 'paid': return 'bg-emerald-50 text-emerald-700 ring-emerald-200';
            case 'verifyingPayment': return 'bg-amber-50 text-amber-800 ring-amber-200';
            case 'partialPayment': return 'bg-orange-50 text-orange-800 ring-orange-200';
            case 'pendingPayment': return 'bg-red-50 text-red-700 ring-red-200';
            default: return 'bg-slate-50 text-slate-600 ring-slate-200';
        }
    }

    hasMembership(athlete: Athlete): boolean {
        const expiration = athleteMembershipExpiration(athlete);
        return !!expiration && expiration.isAfter(moment());
    }

    membershipText(athlete: Athlete): string {
        const expiration = athleteMembershipExpiration(athlete);
        if (!expiration) return 'Mai tesserato';
        return expiration.isAfter(moment())
            ? `Scade il ${expiration.format('DD/MM/YYYY')}`
            : `Scaduto il ${expiration.format('DD/MM/YYYY')}`;
    }

    documentState(athlete: Athlete): {status: string, text: string} {
        return this.utils.remaningDays(athlete.primaryDocExpirationDate ?? '');
    }

    documentClasses(status: string): string {
        switch (status) {
            case 'success': return 'text-emerald-700';
            case 'warning': return 'text-amber-700';
            case 'danger': return 'text-red-600';
            default: return 'text-slate-400';
        }
    }

    trackById(_: number, athlete: Athlete) {
        return athlete.id;
    }

    // ---- Actions ----

    navigateToOnboarding() {
        void this.router.navigate(['/companies/athletes/onboarding']);
    }

    async deleteAthlete(athlete: Athlete) {
        const result = await Swal.fire({
            icon: 'warning',
            title: `Eliminare ${athlete.firstName} ${athlete.lastName}?`,
            text: "L'atleta non comparirà più negli elenchi della società.",
            showCancelButton: true,
            confirmButtonText: 'Sì, elimina',
            cancelButtonText: 'Annulla',
            confirmButtonColor: '#dc2626',
            focusCancel: true,
        });
        if (!result.isConfirmed) return;

        const success = await this.athleteService.deleteAthlete(athlete.id!);
        if (success) {
            Swal.fire({icon: 'success', title: 'Atleta eliminato', timer: 1600, showConfirmButton: false});
            // Step back when the last row of the last page was removed.
            if (this.athletes.length === 1 && this.page > 0) this.page--;
            await this.load();
        } else {
            Swal.fire({icon: 'error', title: "Errore nell'eliminazione dell'atleta"});
        }
    }

    async exportCsv() {
        this.exporting = true;
        const list = await this.athleteService.listAthletes({
            ...this.baseParams(),
            ...this.quickFilterParams(this.quickFilter),
            $skip: 0,
            $top: 1000,
        });
        this.exporting = false;
        if (!list) {
            Swal.fire({icon: 'error', title: "Errore durante l'esportazione"});
            return;
        }

        const seasonName = this.focusSeason?.name ?? '';
        const header = ['Cognome', 'Nome', 'Codice fiscale', 'Email', 'Telefono',
            `Categoria ${seasonName}`.trim(), 'Stato pagamento', 'Quota', 'Tesseramento'];
        if (this.primaryDocumentExists) header.push('Documento');
        const rows = list.rows.map(athlete => {
            const enrollment = this.focusEnrollment(athlete);
            const row = [
                athlete.lastName, athlete.firstName, athlete.fiscalCode, athlete.email, athlete.phone,
                enrollment ? this.categoryLabel(enrollment.categoryId) : '',
                enrollment ? enrollmentStatusLabel(enrollmentDisplayStatus(enrollment)) : 'Non iscritto',
                enrollment ? enrollmentAmount(enrollment).toFixed(2).replace('.', ',') : '',
                this.membershipText(athlete),
            ];
            if (this.primaryDocumentExists) row.push(this.documentState(athlete).text);
            return row;
        });
        // Semicolons + BOM so Excel (Italian locale) opens it with the right columns and accents.
        const csv = [header, ...rows]
            .map(row => row.map(value => `"${(value ?? '').toString().replace(/"/g, '""')}"`).join(';'))
            .join('\r\n');
        const url = URL.createObjectURL(new Blob(['﻿' + csv], {type: 'text/csv;charset=utf-8'}));
        const link = document.createElement('a');
        link.href = url;
        link.download = `atleti-${moment().format('YYYY-MM-DD')}.csv`;
        link.click();
        URL.revokeObjectURL(url);
    }

    // ---- Query building ----

    private baseParams(): Record<string, string | number | boolean> {
        const params: Record<string, string | number | boolean> = {companyId: this.companyId};
        if (this.search.trim()) params['text'] = this.search.trim();
        if (this.selectedSeasonId) params['seasonId'] = this.selectedSeasonId;
        if (this.selectedCategoryId) params['categoryId'] = this.selectedCategoryId;
        return params;
    }

    private quickFilterParams(filter: QuickFilter): Record<string, string | number | boolean> {
        switch (filter) {
            // Unpaid only makes sense within a season: default to the one shown in the table.
            case 'missingPayment':
                return this.focusSeason?.id ? {paymentNotPaid: true, seasonId: this.focusSeason.id} : {paymentNotPaid: true};
            case 'membership': return {membership: 1};
            case 'expiredDocument': return {expiredCertificates: true};
            default: return {};
        }
    }
}
